import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";

import axios from "../axiosConfig";
import { roleOf } from "../auth/permissions";
import { useActiveBusinessId } from "../auth/useActiveBusiness";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { addCommasToNumber } from "../utils";
import CalendarMonth, {
  DAY_MS,
  startOfDay,
  shiftMonth,
  monthOf,
} from "../components/CalendarMonth";
import AppShell from "../components/AppShell";
import { ACTION_KIND, ACTION_LABEL, fieldLabel } from "../components/OrderDetailPanel";
import { useThemeTokens } from "../theme/BrandingProvider";
import "../dashboard-redesign.css";

/** 3200000 -> "$3.2M", 486200 -> "$486.2k", 392 -> "$392" — same tiers
    as the overview page's `money`. */
function compactMoney(value) {
  const n = Number(value) || 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 999950) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 999.95) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs).toLocaleString("en-AU")}`;
}

/* ------------------------------------------------------------------ *
 * Dashboard (redesign) — /dashboard-v2
 *
 * Reorganises the three-column dashboard in src/pages/dashboard.js:
 *   - order counts + type breakdowns   -> KPI strip
 *   - the three detached progress bars -> one production pipeline
 *   - the revenue modal                -> owner-gated dark card, inline
 *   - the top-seller pie               -> donut with share + revenue legend
 *   - (new)                            -> 14-day orders-over-time chart
 *
 * Wired to the same four endpoints the legacy dashboard calls, plus
 * GET /work-orders/daily-counts for the 14-day series and
 * GET /work-orders/history for recent activity.
 * ------------------------------------------------------------------ */

const RANGES = ["Today", "Tomorrow", "This week"];

/* Range popover helpers — same picker as the group overview. dateRange
   holds DD/MM/YYYY strings; the calendar works in local-midnight stamps. */
const toStamp = (ddmmyyyy) => moment(ddmmyyyy, "DD/MM/YYYY").startOf("day").valueOf();
const fromStamp = (t) => moment(t).format("DD/MM/YYYY");
const formatInput = (t) => (t ? new Date(t).toLocaleDateString("en-AU") : "");
const daysBetween = (from, to) => Math.round((to - from) / DAY_MS) + 1;

/* business theme shades (src/theme/theme.css) — the donut is a CSS gradient */
const DONUT_PALETTE = [
  "var(--rf-brand)",
  "var(--rf-brand-lite)",
  "var(--rf-brand-soft)",
  "var(--rf-brand-pale)",
  "var(--rf-brand-edge)",
];

/* Orders over time — GET /work-orders/daily-counts, one point per day
   ending on the selected range's last day. A short range (or one day)
   gets SERIES_DAYS of context; a longer one is shown whole, up to the
   endpoint's SERIES_MAX_DAYS. Each day is counted the way the Orders KPI
   counts a one-day range. */
const SERIES_DAYS = 14;
const SERIES_MAX_DAYS = 60;
const EMPTY_SERIES = { labels: [], funeral: [], retail: [], max: 4, average: null, days: 0, end: null };

function toSeries(response) {
  const days = (response && response.days) || [];
  const peak = days.reduce((m, d) => Math.max(m, d.funeral, d.retail), 0);
  return {
    labels: days.map((d) => moment(d.date, "DD/MM/YYYY").format("D MMM")),
    funeral: days.map((d) => d.funeral),
    retail: days.map((d) => d.retail),
    // a little headroom over the tallest point, never a flat 0-0 axis
    max: Math.max(4, Math.ceil(peak * 1.15)),
    average: response && response.average !== undefined ? response.average : null,
    days: days.length,
    end: days.length ? days[days.length - 1].date : null,
  };
}

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

/* Recent activity — GET /work-orders/history?limit=N, the business-wide
   work-order audit trail (created / updated / deleted / run / transfer
   entries), newest first. Labels are shared with the order detail
   panel's history timeline. */
const ACTIVITY_LIMIT = 8;

const ACTIVITY_TONE = {
  create: "var(--rf-brand)",
  update: "#b0872f",
  delete: "#a8483e",
  run: "#4d7a52",
  transfer: "var(--rf-accent)",
};

/* The name a person would know the order by. */
function orderName(entry) {
  const w = entry.work_order || {};
  const who =
    (w.type === "Funeral" && w.late_person_name) ||
    w.company_name ||
    w.ordered_by ||
    w.recipient_name ||
    w.late_person_name;
  const serial = entry.work_order_serial ? `#${entry.work_order_serial}` : null;
  return [w.type, who, serial].filter(Boolean).join(" · ") || "Work order";
}

/* "just now", "12 minutes ago", "3 hours ago", "2 days ago"; from a week
   out, the date — "Sep 23", with the year once it is not this one. */
function relativeTime(at, now) {
  const mins = Math.floor(moment(now).diff(at, "minutes"));
  if (mins < 1) return "just now";
  const plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"} ago`;
  if (mins < 60) return plural(mins, "minute");
  const hours = Math.floor(mins / 60);
  if (hours < 24) return plural(hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 7) return plural(days, "day");
  return at.format(at.isSame(moment(now), "year") ? "MMM D" : "MMM D, YYYY");
}

/* One history entry -> one feed row. */
function toActivity(entry) {
  const action = entry.action || "updated";
  const actor = entry.actor || {};
  const by = actor.full_name || actor.userName || "System";
  let what = "";
  if (action === "updated") {
    const fields = [
      ...new Set((entry.changes || []).map((c) => fieldLabel(String(c.field).split(".")[0]))),
    ];
    what = fields.length > 3 ? `${fields.slice(0, 3).join(", ")} +${fields.length - 3}` : fields.join(", ");
  } else if ((action === "added_to_run" || action === "removed_from_run") && entry.run) {
    what = [entry.run.drivers_name, entry.run.delivery_date].filter(Boolean).join(" · ");
  }
  const at = moment(entry.created_at);
  return {
    id: entry._id,
    kind: ACTION_KIND[action] || "update",
    title: ACTION_LABEL[action] || fieldLabel(action),
    detail: [orderName(entry), what, by].filter(Boolean).join(" · "),
    at,
    when: at.format("D MMM YYYY, HH:mm"),
  };
}

/* Chart.js config for the 14-day series — smooth lines, gold area fill under
   Funeral, a marker on today's point. Colours are the business theme's
   tokens — Chart.js paints a canvas, so it needs real hex, not var(). */
function buildOrdersChart(series, tokens) {
  const n = series.funeral.length;
  const labels = series.labels;
  const lastPointOnly = (r) => (ctx) => (ctx.dataIndex === n - 1 ? r : 0);
  return {
    data: {
      labels,
      datasets: [
        {
          label: "Funeral",
          data: series.funeral,
          order: 2, // higher order draws first — keeps the fill behind Retail
          borderColor: tokens.brand,
          backgroundColor: `rgba(${tokens["brand-tint-rgb"]}, 0.85)`,
          fill: "origin",
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: lastPointOnly(4.5),
          pointBackgroundColor: tokens.brand,
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointHoverRadius: 4.5,
        },
        {
          label: "Retail",
          data: series.retail,
          order: 1,
          borderColor: tokens.accent,
          fill: false,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { display: false } },
      scales: {
        x: {
          grid: { display: false },
          border: { color: "#f0ece3" },
          ticks: {
            maxTicksLimit: 5,
            maxRotation: 0,
            color: "#a29b8d",
            font: { size: 11, family: "'Instrument Sans', Helvetica, Arial, sans-serif" },
          },
        },
        y: {
          min: 0,
          suggestedMax: series.max,
          grid: { color: "#f0ece3" },
          border: { display: false },
          ticks: { display: false, maxTicksLimit: 5 },
        },
      },
    },
  };
}

/* Conic-gradient stops + per-slice share, derived from raw amounts so the
   donut can never disagree with its legend. */
function buildDonut(items) {
  const total = items.reduce((sum, s) => sum + s.amount, 0);
  if (!total) return null;
  let acc = 0;
  const stops = items.map((s) => {
    const from = (acc / total) * 100;
    acc += s.amount;
    const to = (acc / total) * 100;
    return `${s.color} ${from.toFixed(2)}% ${to.toFixed(2)}%`;
  });
  return {
    total,
    gradient: `conic-gradient(from -90deg, ${stops.join(",")})`,
    rows: items.map((s) => ({
      name: s.name,
      color: s.color,
      value: `$${addCommasToNumber(Math.round(s.amount))}`,
      share: `${Math.round((s.amount / total) * 100)}%`,
    })),
  };
}

function money(v) {
  return `$${addCommasToNumber(Number(v || 0).toFixed(2))}`;
}

/* Walk every ordered product in the by-date-range body and total up the
   work orders marked **UNPAID** (payment_option on the parent work order). */
function deriveUnpaid(products) {
  const seen = new Set();
  let invoices = 0;
  let outstanding = 0;
  const productPrice = (p) =>
    (Number(p.amount) || 0) * Number(p.customPrice ? p.customPrice : p.price || 0) +
    Number(p.sashPrice || 0) +
    Number(p.delivery_fee || 0);
  const consider = (p) => {
    const wo = p.parent_work_order || p;
    if ((wo.payment_option || "") !== "**UNPAID**") return;
    const key = wo._id || wo.serial || wo.id;
    if (key && !seen.has(key)) {
      seen.add(key);
      invoices += 1;
    } else if (!key) {
      invoices += 1;
    }
    outstanding += productPrice(p);
  };
  Object.values((products || {}).Funeral || {}).forEach((group) =>
    (group || []).forEach(consider)
  );
  ((products || {}).Retail || []).forEach(consider);
  return { invoices, outstanding };
}

export function DashboardRedesign() {
  const { user, logout, memberships } = useAuth() || {};
  const navigate = useNavigate();

  /* Revenue and every dollar figure on this page are for owners only. The
     role is the one held in the open business, falling back to the
     session's own role. */
  const activeBusinessId = useActiveBusinessId();
  const activeMembership = (memberships || []).find(
    (m) => String(m.business_id) === String(activeBusinessId)
  );
  const isOwner = roleOf(activeMembership || user) === "owner";

  const [range, setRange] = useState("Today");
  // Custom-range popover — a draft that only lands on Apply.
  const [calOpen, setCalOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(null);
  const [draftTo, setDraftTo] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => monthOf(startOfDay(Date.now())));
  const pickerRef = useRef(null);
  const [dateRange, setDateRange] = useState({
    startDate: moment().format("DD/MM/YYYY"),
    endDate: moment().format("DD/MM/YYYY"),
  });

  // Endpoint state — same shapes the legacy dashboard stores.
  const [progress, setProgress] = useState({}); // /wo/OrderedProductsCount
  const [workOrderCount, setWorkOrderCount] = useState({}); // /work-orders/get-grouped-count
  const [productBody, setProductBody] = useState({}); // /ordered-products/by-date-range
  const [groupedList, setGroupedList] = useState([]); // /ordered-products/by-group
  const [loadingCount, setLoadingCount] = useState(0);
  // The chart follows the selected range (see SERIES_DAYS above). A slow
  // reply for a range already left behind is dropped.
  const [series, setSeries] = useState(EMPTY_SERIES);
  const [seriesLoading, setSeriesLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setSeriesLoading(true);
    const span =
      moment(dateRange.endDate, "DD/MM/YYYY").diff(moment(dateRange.startDate, "DD/MM/YYYY"), "days") + 1;
    axios
      .get(`${BASE_SERVER_URL}/work-orders/daily-counts`, {
        params: {
          end: dateRange.endDate,
          days: Math.min(SERIES_MAX_DAYS, Math.max(SERIES_DAYS, span || 1)),
        },
      })
      .then((res) => {
        if (live && res.data && res.data.status === "success") setSeries(toSeries(res.data.response));
      })
      .catch(() => {
        if (live) setSeries(EMPTY_SERIES);
      })
      .finally(() => {
        if (live) setSeriesLoading(false);
      });
    return () => {
      live = false;
    };
  }, [dateRange]);

  // null until the feed answers; not range-scoped, so fetched once.
  const [activity, setActivity] = useState(null);
  // Re-render each minute so "5 minutes ago" keeps counting.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let live = true;
    axios
      .get(`${BASE_SERVER_URL}/work-orders/history`, { params: { limit: ACTIVITY_LIMIT } })
      .then((res) => {
        if (live) setActivity(((res.data && res.data.response) || []).map(toActivity));
      })
      .catch(() => {
        if (live) setActivity([]);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    setLoadingCount(0);

    let url = BASE_SERVER_URL + "/wo/OrderedProductsCount";
    axios
      .post(url, { date: dateRange.startDate })
      .then((doc) => {
        if (doc.data.status === "success") setProgress(doc.data.response || {});
      })
      .catch(() => {})
      .finally(() => setLoadingCount((prev) => prev + 1));

    url = BASE_SERVER_URL + "/ordered-products/by-date-range";
    axios
      .post(url, { dateRange: dateRange })
      .then((doc) => {
        if (doc.data.status === "success") setProductBody(doc.data.body || {});
      })
      .catch(() => {})
      .finally(() => setLoadingCount((prev) => prev + 1));

    url = BASE_SERVER_URL + "/work-orders/get-grouped-count";
    axios
      .post(url, { date: dateRange })
      .then((doc) => {
        if (doc.data.status === "success")
          setWorkOrderCount(doc.data.response || {});
      })
      .catch(() => {})
      .finally(() => setLoadingCount((prev) => prev + 1));

    url = BASE_SERVER_URL + "/ordered-products/by-group";
    axios
      .post(url, { ...dateRange, group_by: "product_type" })
      .then((response) => {
        setGroupedList(response.data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingCount((prev) => prev + 1));
  }, [dateRange]);

  /* Any range change re-runs the four fetches and the chart's; until all
     of them land every card glares over what it last showed (the first
     load shows the empty layout), like the group overview. */
  const loading = loadingCount < 4 || seriesLoading;

  function pickRange(label) {
    setRange(label);
    setCalOpen(false);
    if (label === "Today") {
      const d = moment().format("DD/MM/YYYY");
      setDateRange({ startDate: d, endDate: d });
    } else if (label === "Tomorrow") {
      const d = moment().add(1, "day").format("DD/MM/YYYY");
      setDateRange({ startDate: d, endDate: d });
    } else if (label === "This week") {
      // Assumed Mon–Sun (handoff open question #4).
      setDateRange({
        startDate: moment().isoWeekday(1).format("DD/MM/YYYY"),
        endDate: moment().isoWeekday(7).format("DD/MM/YYYY"),
      });
    }
  }

  function openCal() {
    const from = toStamp(dateRange.startDate);
    const to = toStamp(dateRange.endDate);
    setDraftFrom(from);
    setDraftTo(to);
    setViewMonth(monthOf(to));
    setCalOpen(true);
  }

  function applyCal() {
    if (!draftFrom || !draftTo) return;
    setDateRange({ startDate: fromStamp(draftFrom), endDate: fromStamp(draftTo) });
    setRange("Custom");
    setCalOpen(false);
  }

  function pickCalDate(stamp) {
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(stamp);
      setDraftTo(null);
    } else if (stamp < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(stamp);
    } else {
      setDraftTo(stamp);
    }
  }

  // Close the range popover on outside click or Escape (discards the draft).
  useEffect(() => {
    if (!calOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setCalOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setCalOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [calOpen]);

  /* ---------- derived view data ---------- */

  const total = Number(workOrderCount.Total || 0);
  const funeral = Number(workOrderCount.Funeral || 0);
  const retail = Number(workOrderCount.Retail || 0);
  const areaCount = workOrderCount.AreaCount || {};

  const productsTotal = Number(progress.total || 0);
  const productsDone = Number(progress.complete || 0);
  const productsOpen = Math.max(0, productsTotal - productsDone);
  const openPct = productsTotal
    ? Math.round((productsOpen / productsTotal) * 100)
    : 0;

  const deliveries = ["North", "South", "West", "City"].map((name) => ({
    name,
    value: Number(areaCount[name] || 0),
  }));
  const deliveriesTotal = deliveries.reduce((sum, a) => sum + a.value, 0);

  const unpaid = useMemo(
    () => deriveUnpaid(productBody.products),
    [productBody]
  );

  const kpis = [
    {
      label: "Orders",
      value: String(total),
      unit: dateRange.startDate === dateRange.endDate ? "on the day" : "in range",
      sub: `${funeral} funeral · ${retail} retail`,
    },
    {
      label: "Products",
      value: String(productsTotal),
      unit: "to make",
      sub: `${productsDone} finished, ${productsOpen} open`,
      delta: `${openPct}% open`,
      tone: openPct > 50 ? "warn" : "up",
    },
    {
      label: "Deliveries",
      value: String(deliveriesTotal),
      unit: "to run",
      sub: `across ${deliveries.filter((a) => a.value > 0).length || 0} areas`,
    },
    {
      label: "Unpaid",
      value: String(unpaid.invoices),
      unit: "invoices",
      sub: isOwner
        ? `${money(unpaid.outstanding)} outstanding`
        : "awaiting payment",
      delta: unpaid.invoices > 0 ? "chase" : "clear",
      tone: unpaid.invoices > 0 ? "down" : "up",
    },
  ];

  /* Cumulative funnel from /wo/OrderedProductsCount — the same three stages
     the legacy dashboard tracks (delivered has no data source yet, matching
     the old page which hardcodes it to 0). */
  const pipeline = useMemo(() => {
    const stageColor = (amount, outof) => {
      const pct = outof ? (amount / outof) * 100 : 0;
      return pct > 75 ? "#4d7a52" : pct > 50 ? "#b0872f" : "#a8483e";
    };
    const finalCheck = Number(progress.finalCheck || 0);
    const stages = [
      { task: "Complete", sub: "made on the bench", amount: productsDone, outof: productsTotal },
      { task: "Final check", sub: "ready for delivery", amount: finalCheck, outof: productsDone },
      { task: "Delivered", sub: "signed off by driver", amount: 0, outof: finalCheck },
    ];
    return stages.map((s) => ({ ...s, color: stageColor(s.amount, s.outof) }));
  }, [progress, productsDone, productsTotal]);

  /* Top sellers from /ordered-products/by-group — top 4 by revenue plus an
     aggregated Other slice, colours assigned in rank order. */
  const donut = useMemo(() => {
    const rows = (groupedList || [])
      .filter((g) => Number(g.totalProfit) > 0)
      .sort((a, b) => Number(b.totalProfit) - Number(a.totalProfit));
    const top = rows.slice(0, 4).map((g, i) => ({
      name: g._id || "Untyped",
      amount: Number(g.totalProfit),
      color: DONUT_PALETTE[i],
    }));
    const rest = rows.slice(4).reduce((sum, g) => sum + Number(g.totalProfit), 0);
    if (rest > 0) top.push({ name: "Other", amount: rest, color: DONUT_PALETTE[4] });
    return buildDonut(top);
  }, [groupedList]);

  const revenue = useMemo(() => {
    const revenueStrat = workOrderCount.RevenueStrat || {};
    const gross = Number(revenueStrat.estimated_gross_profit || 0);
    const wage = Number(revenueStrat.estimated_wage || 0);
    const rows = [
      { k: "Total revenue", v: money(revenueStrat.total_all) },
      /* Only Funeral orders with the commission box ticked contribute.
         Shown so the deduction below is visible rather than implied. */
      { k: "Commission", v: money(revenueStrat.total_commission || 0) },
      {
        k: "After GST & commission",
        /* the spelled key is the new one; the misspelling stays for now */
        v: money(
          revenueStrat.total_minus_gst_and_commission !== undefined
            ? revenueStrat.total_minus_gst_and_commission
            : revenueStrat.total_minus_gst_and_commsion
        ),
      },
      { k: "Cost of goods", v: money(revenueStrat.total_cost_of_goods) },
    ];
    if (wage) rows.push({ k: "Wages", v: `−${money(wage)}` });
    if (total)
      rows.push({
        k: "Average order value",
        v: money(Number(revenueStrat.total_all || 0) / total),
      });
    return { gross: money(gross), net: money(gross - wage), rows };
  }, [workOrderCount, total]);

  const themeTokens = useThemeTokens();
  const ordersChart = useMemo(() => buildOrdersChart(series, themeTokens), [series, themeTokens]);

  const rangeLabel = (() => {
    const from = moment(dateRange.startDate, "DD/MM/YYYY");
    const to = moment(dateRange.endDate, "DD/MM/YYYY");
    if (from.isSame(to, "day")) return from.format("D MMM YYYY");
    if (from.year() !== to.year()) return `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;
    return `${from.format("D MMM")} – ${to.format("D MMM YYYY")}`;
  })();
  const rangeDays = (() => {
    const n = daysBetween(toStamp(dateRange.startDate), toStamp(dateRange.endDate));
    return `${n} ${n === 1 ? "day" : "days"}`;
  })();

  const dateMeta =
    dateRange.startDate === dateRange.endDate
      ? moment(dateRange.startDate, "DD/MM/YYYY").format("dddd D MMMM YYYY")
      : `${moment(dateRange.startDate, "DD/MM/YYYY").format("D MMM")} – ${moment(
          dateRange.endDate,
          "DD/MM/YYYY"
        ).format("D MMM YYYY")}`;

  function printDaySheets() {
    // Same print route the work-order list uses for the day sheet.
    window.open(
      "/workorders/print/" +
        JSON.stringify({
          filter: {
            funeral_date: dateRange.startDate.replaceAll("/", "-"),
            type: "Funeral",
          },
        }),
      "_blank"
    );
  }

  return (
    <AppShell
      title="Today"
      user={user}
      counts={{ workOrders: total, orderedProducts: productsTotal }}
      onLogout={logout}
    >
      <div className={`rf-dash${loading ? " is-loading" : ""}`} aria-busy={loading}>
        {/* ---------- range + actions ---------- */}
        <div className="rf-dash-bar">
          <div className="rf-datepicker" ref={pickerRef}>
            <div className="rf-segmented">
              {RANGES.map((label) => (
                <button
                  key={label}
                  type="button"
                  className={range === label ? "is-active" : ""}
                  onClick={() => pickRange(label)}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={`rf-range-btn${calOpen || range === "Custom" ? " is-open" : ""}`}
              onClick={() => (calOpen ? setCalOpen(false) : openCal())}
              aria-expanded={calOpen}
            >
              <span className="rf-range-label">{rangeLabel}</span>
              <span className="rf-range-days">{rangeDays}</span>
              <span className="rf-range-caret">▾</span>
            </button>

            {calOpen && (
              <div className="rf-rangecal">
                <div className="rf-rangecal-head">
                  <span>Custom range</span>
                  <button
                    type="button"
                    className="rf-rangecal-close"
                    onClick={() => setCalOpen(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <div className="rf-rangecal-inputs">
                  <div className="rf-rangecal-field">
                    <label>From</label>
                    <input readOnly value={formatInput(draftFrom)} />
                  </div>
                  <div className="rf-rangecal-field">
                    <label>To</label>
                    <input readOnly value={formatInput(draftTo)} />
                  </div>
                </div>

                <div className="rf-rangecal-months">
                  <CalendarMonth
                    year={shiftMonth(viewMonth, -1).year}
                    monthIndex={shiftMonth(viewMonth, -1).monthIndex}
                    from={draftFrom}
                    to={draftTo}
                    onPick={pickCalDate}
                    onPrev={() => setViewMonth((m) => shiftMonth(m, -1))}
                  />
                  <CalendarMonth
                    year={viewMonth.year}
                    monthIndex={viewMonth.monthIndex}
                    from={draftFrom}
                    to={draftTo}
                    onPick={pickCalDate}
                    onNext={() => setViewMonth((m) => shiftMonth(m, 1))}
                  />
                </div>

                <div className="rf-rangecal-foot">
                  <span>
                    {draftFrom && draftTo
                      ? `${daysBetween(draftFrom, draftTo)} days selected`
                      : "Pick an end date"}
                  </span>
                  <button
                    type="button"
                    className="rf-btn rf-btn-ghost"
                    onClick={() => setCalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rf-btn rf-btn-primary"
                    onClick={applyCal}
                    disabled={!draftFrom || !draftTo}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rf-dash-datemeta">
            {dateMeta} · <b>{total} orders</b> on the bench
          </div>

          <span className="rf-spacer" />

          <button type="button" className="rf-btn rf-btn-ghost" onClick={printDaySheets}>
            Print day sheets
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-primary"
            onClick={() => navigate("/new-order")}
          >
            New order
          </button>
        </div>

        {/* ---------- KPI strip ---------- */}
        <div className="rf-kpis">
          {kpis.map((k) => (
            <div key={k.label} className="rf-kpi">
              <div className="rf-kpi-top">
                <span className="rf-kpi-label">{k.label}</span>
                {k.delta && (
                  <span className={`rf-kpi-chip tone-${k.tone}`}>{k.delta}</span>
                )}
              </div>
              <div className="rf-kpi-value">
                <span className="rf-serif rf-figure rf-num">{k.value}</span>
                <span className="rf-kpi-unit">{k.unit}</span>
              </div>
              <div className="rf-kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ---------- two columns ---------- */}
        <div className="rf-dash-grid">
          {/* left */}
          <div className="rf-dash-col">
            <section className="rf-card">
              <div className="rf-card-head" style={{ marginBottom: 16 }}>
                <span className="rf-card-title">Production pipeline</span>
                <span className="rf-card-meta">
                  {productsTotal} products across {total} orders
                </span>
              </div>

              <div className="rf-pipeline">
                {pipeline.map((p) => {
                  const pct = p.outof
                    ? Math.min(100, Math.round((p.amount / p.outof) * 100))
                    : 0;
                  return (
                    <div key={p.task} className="rf-pipeline-row">
                      <div className="rf-pipeline-label">
                        <div className="rf-pipeline-task">{p.task}</div>
                        <div className="rf-pipeline-sub">{p.sub}</div>
                      </div>
                      <div className="rf-track">
                        <span style={{ width: `${pct}%`, background: p.color }} />
                      </div>
                      <div className="rf-pipeline-count">
                        <b>{p.amount}</b>
                        <span> / {p.outof || 0}</span>
                      </div>
                      <div className="rf-pipeline-pct" style={{ color: p.color }}>
                        {pct}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rf-card rf-chart-card">
              <div className="rf-chart-head">
                <span className="rf-card-title">Orders over time</span>
                <span className="rf-card-meta" style={{ paddingLeft: 0 }}>
                  {series.end
                    ? `${series.days} days to ${moment(series.end, "DD/MM/YYYY").format("D MMM")}`
                    : "loading…"}
                </span>
                <span className="rf-spacer" />
                <div className="rf-legend-group">
                  <span className="rf-legend">
                    <i style={{ background: "var(--rf-brand)" }} />
                    Funeral
                  </span>
                  <span className="rf-legend">
                    <i style={{ background: "var(--rf-accent)" }} />
                    Retail
                  </span>
                </div>
              </div>

              <div className="rf-chart-lede">
                <span className="rf-serif rf-figure rf-num">{total}</span>
                <span>
                  orders {range === "Today" ? "today" : "in range"}
                  {series.average !== null && ` · ${series.days}-day average ${series.average}`}
                </span>
              </div>

              <div className="rf-chart">
                <Line data={ordersChart.data} options={ordersChart.options} />
              </div>
            </section>

            <section className="rf-card">
              <div className="rf-card-head" style={{ marginBottom: 12 }}>
                <span className="rf-card-title">Recent activity</span>
              </div>

              {activity && activity.length > 0 ? (
                <div className="rf-activity">
                  {activity.map((a) => (
                    <div key={a.id} className="rf-activity-row">
                      <span
                        className="rf-activity-dot"
                        style={{ background: ACTIVITY_TONE[a.kind] }}
                      />
                      <div className="rf-activity-text">
                        <div className="rf-activity-title">{a.title}</div>
                        <div className="rf-activity-detail">{a.detail}</div>
                      </div>
                      <span className="rf-activity-time rf-num" title={a.when}>
                        {relativeTime(a.at, now)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rf-empty">
                  {activity ? "No work order activity yet" : "Loading activity…"}
                </div>
              )}
            </section>
          </div>

          {/* right */}
          <div className="rf-dash-col">
            {isOwner && (
              <section className="rf-revenue">
                <div className="rf-card-head">
                  <span className="rf-card-title">Expected revenue</span>
                  <span className="rf-revenue-owner">Owner only</span>
                </div>

                <div className="rf-revenue-figures">
                  <div>
                    <div className="rf-revenue-label">Gross</div>
                    <div className="rf-serif rf-revenue-figure is-gross">
                      {revenue.gross}
                    </div>
                  </div>
                  <div className="rf-revenue-rule" />
                  <div>
                    <div className="rf-revenue-label">Net</div>
                    <div className="rf-serif rf-revenue-figure is-net">
                      {revenue.net}
                    </div>
                  </div>
                </div>

                <dl style={{ margin: 0 }}>
                  {revenue.rows.map((r) => (
                    <div key={r.k} className="rf-revenue-row">
                      <dt>{r.k}</dt>
                      <dd>{r.v}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* Ranked and sized by revenue, so owners only too. */}
            {isOwner && (
            <section className="rf-card rf-sellers-card">
              <div className="rf-card-head">
                <span className="rf-card-title">Top sellers</span>
                <span className="rf-card-meta">by revenue</span>
              </div>

              {donut ? (
                <div className="rf-sellers">
                  <div className="rf-donut" style={{ background: donut.gradient }}>
                    <div className="rf-donut-hole">
                      <span className="rf-serif rf-donut-total">
                        {compactMoney(donut.total)}
                      </span>
                      <span className="rf-donut-caption">total</span>
                    </div>
                  </div>

                  <div className="rf-seller-list">
                    {donut.rows.map((s) => (
                      <div key={s.name} className="rf-seller">
                        <span className="rf-swatch" style={{ background: s.color }} />
                        <span className="rf-seller-name">{s.name}</span>
                        <span className="rf-seller-share">{s.share}</span>
                        <span className="rf-seller-value">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rf-empty">No sales in this range yet</div>
              )}
            </section>
            )}

            <section className="rf-card rf-areas-card">
              <div className="rf-areas-title">Deliveries by area</div>
              <div className="rf-areas">
                {deliveries.map((a) => (
                  <div key={a.name} className="rf-area">
                    <div className="rf-serif rf-area-value">{a.value}</div>
                    <div className="rf-area-name">{a.name}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

export default DashboardRedesign;
